<script setup lang="ts" generic="TColumn">
import {
  onMounted,
  onUpdated,
  toRef,
  useTemplateRef,
} from 'vue';

import VLoader from '@components/loader/VLoader.vue';
import VTableCellFormatter from '@modules/tables/components/VTableCellFormatter/VTableCellFormatter.vue';
import VTableEmptyDisplay from '@modules/tables/components/VTableEmptyDisplay/VTableEmptyDisplay.vue';
import VTableFilter from '@modules/tables/components/VTableFilter/VTableFilter.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';
import VTableOrder from '@modules/tables/components/VTableOrder/VTableOrder.vue';
import VTablePagination from '@modules/tables/components/VTablePagination/VTablePagination.vue';
import VTableSearch from '@modules/tables/components/VTableSearch/VTableSearch.vue';
import VTableTitle from '@modules/tables/components/VTableTitle/VTableTitle.vue';
import { useTable } from '@modules/tables/composables';
import { QA_TABLES } from '@modules/tables/constants';
import {
  formatTotalColumnResult,
  localeKeyTypeGuard,
  paginationTypeGuard,
} from '@modules/tables/utils';

import type {
  ITableEmits,
  ITableProps,
  ITableSlots,
} from '../VTable/types';
import VTableRefresh from '../VTableRefresh/VTableRefresh.vue';

const props = withDefaults(defineProps<ITableProps<TColumn>>(), {
  rowsOnPage: 10,
  multiselect: () => ({}),
  state: () => ({
    page: 1,
  }),
  selectedIds: () => ([]),
  totalLabelTPath: '',
  subtotalLabelTPath: '',
  fullHeight: false,
});

const emit = defineEmits<ITableEmits>();

defineSlots<ITableSlots<TColumn>>();

const dataTestTableWrapper = { el: QA_TABLES.root };

const {
  selection,
  isMultiselectEnabled,
  selectionMode,
  columns,
  list,
  totalColumns,
  subTotalData,
  dataIsEmpty,
  selectionIdsOnPage,
  defaultCellPt,
  filterColumnOptions,
  isSearchEnabled,
  transitionDelayMS,
  transitionMaxWaitMS,
  isIdColumnExistedInList,
  overlayPanel,
  isVisible,
  tableCellContent,
  tablePt,
  multiselectColumnPt,
  multiselectActionsColumnPt,
  dataTestCells,
  handleMultiselectAction,
  handlePagination,
  handleSearch,
  handleOrder,
  applyFilter,
  clearFilter,
  onSelect,
  getTotalCellPt,
  toggleOverlay,
} = useTable(
  toRef(props, 'data'),
  toRef(props, 'state'),
  toRef(props, 'multiselect'),
  toRef(props, 'selectedIds'),
  toRef(props, 'columnsOptions'),
  toRef(props, 'subtotalEnabled'),
  toRef(props, 'isFetching'),
  emit,
);

onMounted(() => {
  // delay render to prevent freeze
  if (props.data) {
    setTimeout(() => {
      isVisible.value = true;
    }, transitionDelayMS);
  }

  // show table anyway after max wait
  setTimeout(() => {
    if (!isVisible.value) {
      isVisible.value = true;
    }
  }, transitionMaxWaitMS);
});

onUpdated(() => {
  if (!isVisible.value) {
    isVisible.value = !!props.data;
  }
});

const wrapper = useTemplateRef('wrapper');

function onKeydown(event: KeyboardEvent) {
  if (document.activeElement instanceof HTMLInputElement) {
    return;
  }

  const selection = window.getSelection();
  if ((event.metaKey || event.ctrlKey || event.altKey) && selection) {
    if (event.code === 'KeyC') {
      void navigator.clipboard.writeText(selection.toString());
    } else if (event.code === 'KeyA') {
      const table = wrapper.value?.querySelector('table');

      if (table) {
        const range = document.createRange();
        range.selectNodeContents(table);

        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'VTableDesktop'"
    ref="wrapper"
    v-data-test="dataTestTableWrapper"
    @keydown.stop="onKeydown"
    @click="toggleOverlay"
  >
    <PDataTable
      v-model:selection="selection"
      class="p-datatable-sm"
      :data-key="isMultiselectEnabled || isIdColumnExistedInList ? 'id' : undefined"
      :value="list"
      :selection-mode="selectionMode"
      :meta-key-selection="false"
      scrollable
      :scroll-height="!fullHeight ? subtotalEnabled && subTotalData ? '665px' : '615px' : undefined"
      resizable-columns
      column-resize-mode="fit"
      :row-hover="!!data"
      striped-rows
      :pt="tablePt"
      @row-select="onSelect"
      @row-unselect="onSelect"
      @row-select-all="onSelect"
      @row-unselect-all="onSelect"
      @row-keydown="onKeydown"
    >
      <PColumn
        v-if="data && isMultiselectEnabled"
        frozen
        align-frozen="left"
        selection-mode="multiple"
        class="w-[60px] min-w-[60px]"
        :pt="multiselectColumnPt"
      />
      <PColumn
        v-if="data && isMultiselectEnabled"
        frozen
        align-frozen="left"
        :pt="multiselectActionsColumnPt"
      >
        <template #header>
          <VTableMultiselectActions
            :options="multiselect"
            :disabled="dataIsEmpty || disableAllControls || selectionIdsOnPage?.length === 0"
            @select="handleMultiselectAction"
          />
        </template>
      </PColumn>
      <PColumn
        v-for="col in columns"
        :key="col.id"
        :field="col.name"
        :pt="defaultCellPt"
      >
        <template #header>
          <span
            v-data-test="dataTestCells.head[col.name]"
            class="p-column-title p-datatable-column-title flex items-center gap-2"
          >
            <span>{{ col.label }}</span>
            <VTableOrder
              v-if="!disableSort"
              :column="col"
              :table-state="state"
              :disabled="dataIsEmpty || loading || disableAllControls"
              @order-update="handleOrder"
            />
          </span>
        </template>
        <template #body="item">
          <slot
            :name="`column:${col.name}`"
            :list-item="item.data"
            :column="col"
            :value="item.data[col.name]"
            :table-state="state"
          >
            <VTableCellFormatter
              v-data-test="dataTestCells.body[col.name]"
              :column="col"
              :value="item.data[col.name]"
              :row-data="item.data"
              :search-query="state?.search?.keyword"
            />
          </slot>
        </template>
      </PColumn>
      <PColumn
        v-if="data && $slots.recordAction"
        frozen
        align-frozen="right"
        body-class="!max-w-none"
        :pt="defaultCellPt"
      >
        <template #body="item">
          <div class="flex items-center justify-end gap-2">
            <slot
              name="recordAction"
              :table-state="state"
              :list-item="item.data"
            />
          </div>
        </template>
      </PColumn>
      <PColumnGroup
        v-if="data?.sum_columns && !dataIsEmpty"
        type="footer"
      >
        <PRow v-if="subtotalEnabled && subTotalData">
          <PColumn
            :footer="$t(subtotalLabelTPath)"
            :pt="getTotalCellPt('transaction_date_for_reports')"
          />
          <PColumn
            v-for="col in totalColumns"
            :key="col.id"
            :footer="formatTotalColumnResult(subTotalData[col.name], col.name)"
            :pt="getTotalCellPt(col.name)"
          />
          <PColumn
            v-if="data && $slots.recordAction"
            :pt="getTotalCellPt('last')"
          />
        </PRow>
        <PRow :pt="{ root: { 'data-test-el': QA_TABLES.bodyRowTotal } }">
          <PColumn
            :footer="$t(totalLabelTPath)"
            :pt="getTotalCellPt('transaction_date_for_reports')"
          />
          <PColumn
            v-for="col in totalColumns"
            :key="col.id"
            :footer="formatTotalColumnResult(data?.sum_columns[col.name], col.name)"
            :pt="getTotalCellPt(col.name)"
          />
          <PColumn
            v-if="data && $slots.recordAction"
            :pt="getTotalCellPt('last')"
          />
        </PRow>
      </PColumnGroup>
      <template #empty>
        <VTableEmptyDisplay
          v-if="data"
          :empty-label-t-path="emptyLabelTPath"
        />
        <VLoader
          :loading="!data"
          mode="structural"
        />
      </template>
      <template #header>
        <div
          class="px-3 pb-2 pt-5"
          :class="filterProps ? undefined : 'pr-5'"
        >
          <div class="flex items-start justify-between">
            <div class="flex-auto word-break">
              <VTableTitle :title="titleTPath" />
            </div>
            <div class="flex items-center gap-2">
              <slot
                :table-state="state"
                name="searchFilter"
              >
                <VTableSearch
                  v-if="data && isSearchEnabled"
                  class="lg:w-[30rem]"
                  :search-query="state?.search?.keyword"
                  :disabled="disableAllControls"
                  @search-update="handleSearch"
                />
                <VTableFilter
                  v-if="localeKeyTypeGuard(data) && filterProps"
                  :filter-set-id="state?.filter_set_id"
                  :translation-path="data.locale_key"
                  v-bind="filterProps"
                  :column-options="filterColumnOptions"
                  :disabled="disableAllControls"
                  @apply-filter="applyFilter"
                  @clear-filter="clearFilter"
                />
                <VTableRefresh
                  v-if="data && refreshButton"
                  :loading="isFetching"
                  @click="$emit('refresh', state)"
                />
              </slot>
            </div>
          </div>
        </div>
        <div class="px-5">
          <slot
            :table-state="state"
            name="tabs"
          />
        </div>
      </template>
    </PDataTable>
    <div class="flex min-h-[75px] content-center items-center justify-between px-4 py-3">
      <div class="flex gap-2">
        <slot
          :table-state="state"
          name="footerButtons"
        />
      </div>
      <div v-if="!disablePagination && paginationTypeGuard(data) && rowsOnPage">
        <VTablePagination
          :start-row-num="data.start_row_num"
          :end-row-num="data.end_row_num"
          :total-pages="data.total_pages"
          :total-entries="data.total_entries"
          :rows-on-page="rowsOnPage"
          :data-is-empty="dataIsEmpty"
          :disabled="loading || disableAllControls"
          :page="state?.page || 1"
          @pagination-update="handlePagination"
        />
      </div>
    </div>
    <POverlayPanel ref="overlayPanel">
      <span
        class="text-editor word-break"
        v-html="tableCellContent"
      />
    </POverlayPanel>
  </div>
</template>
